import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LangProvider } from './context/LangContext';
import { VisitorProvider, useVisitor } from './context/VisitorContext';
import GoogleLoginModal from './components/GoogleLoginModal';
import Home from './pages/Home';
import Tools from './pages/Tools';
import ResourceDetail from './pages/ResourceDetail';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import Resources from './pages/admin/Resources';
import ResourceForm from './pages/admin/ResourceForm';
import Import from './pages/admin/Import';
import Categories from './pages/admin/Categories';
import Users from './pages/admin/Users';
import Settings from './pages/admin/Settings';
import Visitors from './pages/admin/Visitors';
import './App.css';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-page">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" />;
  return children;
}

// Auto-prompt login for visitors on public pages
function VisitorLoginPrompt() {
  const { visitor, promptLogin } = useVisitor();
  const location = useLocation();

  useEffect(() => {
    // Only prompt on public pages, not admin
    if (visitor || location.pathname.startsWith('/admin')) return;

    // Check if dismissed in last 24 hours
    const dismissed = localStorage.getItem('gglancc_login_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return;

    // Show after 5 seconds of browsing
    const timer = setTimeout(() => promptLogin(), 5000);
    return () => clearTimeout(timer);
  }, [visitor, promptLogin, location.pathname]);

  return <GoogleLoginModal />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/resource/:slug" element={<ResourceDetail />} />

      {/* Auth */}
      <Route path="/admin/login" element={user ? <Navigate to="/admin" /> : <Login />} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
      <Route path="/admin/resources/new" element={<ProtectedRoute><ResourceForm /></ProtectedRoute>} />
      <Route path="/admin/resources/edit/:id" element={<ProtectedRoute><ResourceForm /></ProtectedRoute>} />
      <Route path="/admin/import" element={<ProtectedRoute roles={['admin', 'editor']}><Import /></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute roles={['admin', 'editor']}><Categories /></ProtectedRoute>} />
      <Route path="/admin/visitors" element={<ProtectedRoute roles={['admin', 'editor']}><Visitors /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><Users /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={['admin']}><Settings /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <VisitorProvider>
          <Router>
            <VisitorLoginPrompt />
            <AppRoutes />
          </Router>
        </VisitorProvider>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
